import { displayCurrency, displayDistributions } from 'app/scripts/PricingHelper';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import ObjectInfo from 'assets/templates/objectInfo/ObjectInfo';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { isValidPhoneNumber, isValidValue } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty, isNull, pick, sumBy, values } from 'lodash';
import ContractConsentStatus from 'models/enums/ContractConsentStatus';
import PurchaseState from 'models/enums/PurchaseState';
import Subfee from 'models/productManagement/subfee/Model.Subfee';
import Contract from 'models/sales/contract/Model.Contract';
import statementGeneratePdfApi from 'models/sales/statement/generatePdf/Api.Statement.GeneratePdf';
import { ReactNode } from 'react';

type Props = {
  contract: Partial<Model.IContract>;
};
export default function ContractInfoDetails({ contract }: Props) {
  const { lang } = getLocales();
  const { isDealerOrDistributor, isDealer } = useAuthStore();

  if (!contract) return null;

  const { payload } = useApi({
    action: Subfee.list,
    body: {
      id: contract?.subfees?.map((it) => Number(it.subfeeId)),
    },
  });
  const subfees = payload?.data;

  const taxExemptPrice = new Contract(contract).taxExemptPrices;
  async function generateStatementPdf(statementId: number, statementNumber: number) {
    const res = await statementGeneratePdfApi(statementId);

    if (res?.status === HttpStatus.ok) {
      autoTriggerDownload({
        url: generateDataUrl(MimeType.appPDF, res?.payload),
        name: `${lang.statement} #${statementNumber}.pdf`,
      });
      addLog({ success: lang.downloadWasSuccessful });
    } else addLog({ error: res.message });
  }

  const renderExempt = (children: ReactNode) => {
    return contract.client?.taxExempt ? `$${lang.exempt.toUpperCase()}` : children;
  };

  return (
    <>
      <h1>{contract.product?.name || lang.unknownProduct}</h1>
      <div>
        {lang.priceCAD}:{' '}
        <b>
          <FormatValue value={`${(contract.priceCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      {!isDealerOrDistributor && contract.originalPriceCAD !== contract.priceCAD && (
        <div>
          {lang.basePrice}:{' '}
          <b>
            <FormatValue value={`${(contract.originalPriceCAD || 0).toFixed(2)}$`} />
          </b>
        </div>
      )}
      {!isDealerOrDistributor && !!contract.discountPriceCAD && (
        <div>
          {lang.adjustedPrice}:{' '}
          <b>
            <FormatValue value={`${(contract.discountPriceCAD || 0).toFixed(2)}$`} />
          </b>
        </div>
      )}
      {!isDealer && !!contract.distributorPriceCAD && (
        <div>
          {lang.distributorPrice}:{' '}
          <b>
            <FormatValue value={`${(contract.distributorPriceCAD || 0).toFixed(2)}$`} />
          </b>
        </div>
      )}
      <div>
        {lang.dealerCostPst}:{' '}
        <b>
          <FormatValue value={`${((contract.dealerCostProvincialSalesTax || 0) * 100).toFixed(2)}%`} />
        </b>
      </div>
      <div>
        {lang.dealerCostGstHst}:{' '}
        <b>
          <FormatValue value={`${((contract.dealerCostGstHst || 0) * 100).toFixed(2)}%`} />
        </b>
      </div>
      <div>
        {lang.dealerRemittancePst}:{' '}
        <b>
          <FormatValue value={`${((contract.dealerRemittanceProvincialSalesTax || 0) * 100).toFixed(2)}%`} />
        </b>
      </div>
      <div>
        {lang.dealerRemittanceGstHst}:{' '}
        <b>
          <FormatValue value={`${((contract.dealerRemittanceGstHst || 0) * 100).toFixed(2)}%`} />
        </b>
      </div>
      <div>
        {lang.dealerProfitPst}:{' '}
        <b>
          <FormatValue value={`${((contract.dealerProfitProvincialSalesTax || 0) * 100).toFixed(2)}%`} />
        </b>
      </div>
      <div>
        {lang.dealerProfitGstHst}:{' '}
        <b>
          <FormatValue value={`${((contract.dealerProfitGstHst || 0) * 100).toFixed(2)}%`} />
        </b>
      </div>
      <h3>{lang.retailSalePrice}</h3>
      <div>
        {lang.amount}:{' '}
        <b>
          <FormatValue value={`${(contract.salePriceCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      <div>
        {lang.tax}: <b>{renderExempt(<FormatValue value={`${(contract.totalTaxCAD || 0).toFixed(2)}$`} />)}</b>
      </div>
      <hr />
      <div>
        {lang.total}:{' '}
        <b>
          <FormatValue value={`${(taxExemptPrice.salePriceTotalCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      <h3>{lang.dealerCost}</h3>
      <div>
        {lang.amount}:{' '}
        <b>
          <FormatValue value={`${(contract.dealerCost || 0).toFixed(2)}$`} />
        </b>
      </div>
      <div>
        {lang.tax}: <b>{renderExempt(<FormatValue value={`${(contract.dealerCostTaxAmt || 0).toFixed(2)}$`} />)}</b>
      </div>
      <hr />
      <div>
        {lang.total}:{' '}
        <b>
          <FormatValue value={`${(contract.dealerCostTotal || 0).toFixed(2)}$`} />
        </b>
      </div>
      <h3>{lang.dealerRemittance}</h3>
      <div>
        {lang.amount}:{' '}
        <b>
          <FormatValue value={`${(contract.paymentAmountCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      <div>
        {lang.tax}:{' '}
        <b>
          <FormatValue value={`${(contract.paymentTaxCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      <hr />
      <div>
        {lang.total}:{' '}
        <b>
          <FormatValue value={`${(contract.paymentAmountWithTaxCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      <h3>{lang.dealerProfit}</h3>
      <div>
        {lang.amount}:{' '}
        <b>
          <FormatValue value={`${(contract.dealerProfit || 0).toFixed(2)}$`} />
        </b>
      </div>
      <div>
        {lang.tax}: <b>{renderExempt(<FormatValue value={`${(contract.dealerProfitTaxAmt || 0).toFixed(2)}$`} />)}</b>
      </div>
      <hr />
      <div>
        {lang.total}:{' '}
        <b>
          <FormatValue value={`${(contract.dealerProfitWithTaxCAD || 0).toFixed(2)}$`} />
        </b>
      </div>
      <h3>{lang.paymentOptions}</h3>
      {contract.termInMonths && (
        <div>
          {lang.term}:{' '}
          <b>
            <FormatValue value={`${contract.termInMonths}m`} />
          </b>
        </div>
      )}
      {contract.maxKm && (
        <div>
          {lang.maxKm}:{' '}
          <b>
            <FormatValue value={`${contract.maxKm}km`} />
          </b>
        </div>
      )}
      {contract.coverageKm && (
        <div>
          {lang.coverageKm}:{' '}
          <b>
            <FormatValue value={`${contract.coverageKm}km`} />
          </b>
        </div>
      )}
      {contract.maxCoverageKm && (
        <div>
          {lang.maxCoverageKm}:{' '}
          <b>
            <FormatValue value={`${contract.maxCoverageKm}km`} />
          </b>
        </div>
      )}
      {contract.deductible && (
        <div>
          {lang.deductible}: <b>{contract.deductible}</b>
        </div>
      )}
      {contract.customInfo && (
        <div>
          {lang.customInfo}: <b>{contract.customInfo}</b>
        </div>
      )}
      {contract.customInfoGroup && (
        <div>
          {lang.customInfoGroup}: <b>{contract.customInfoGroup}</b>
        </div>
      )}
      {isValidValue(contract.minVehiclePriceCAD) && (
        <div>
          {lang.minPrice}:{' '}
          <b>
            <FormatValue value={`${contract.minVehiclePriceCAD.toFixed(2)}$`} />
          </b>
        </div>
      )}
      {isValidValue(contract.maxVehiclePriceCAD) && (
        <div>
          {lang.maxPrice}:{' '}
          <b>
            <FormatValue value={`${contract.maxVehiclePriceCAD.toFixed(2)}$`} />
          </b>
        </div>
      )}
      {contract.tier && (
        <div>
          {lang.tier}: <b>{contract.tier}</b>
        </div>
      )}
      {contract.class && (
        <div>
          {lang.class}: <b>{contract.class}</b>
        </div>
      )}
      {contract.warrantyOption && (
        <div>
          {lang.warrantyOptions}: <b>{contract.warrantyOption}</b>
        </div>
      )}
      {!isDealerOrDistributor && !!values(contract.pricingDistributions).length && (
        <ObjectInfo
          object={displayDistributions(contract.pricingDistributions)}
          objectName={lang.pricingDistributions}
          disableTranslation
        />
      )}
      {!isDealer && !!values(contract.distributorDistributions).length && (
        <ObjectInfo
          object={displayDistributions(contract.distributorDistributions)}
          objectName={lang.distributorDistributions}
          disableTranslation
        />
      )}
      {contract.subfees && !isEmpty(contract.subfees) && (
        <>
          <h1>{lang.surcharges}</h1>
          <DataMap
            data={contract.subfees}
            render={({ data: subfee }) => (
              <div key={subfee?.subfeeId}>
                {subfees?.find((s) => s.id.toString() === subfee?.subfeeId?.toString())?.name || lang.unknown}:{' '}
                <b>
                  {subfee?.quantity} x <FormatValue value={displayCurrency(subfee?.amount, subfee?.currency)} />
                </b>
              </div>
            )}
          />
          <hr />
          <div>
            {lang.total}:{' '}
            <b>
              <FormatValue value={`$${(contract.surchargesPriceCAD || 0).toFixed(2)}`} />
            </b>
          </div>
        </>
      )}
      {contract.contractEnhancements && !isEmpty(contract.contractEnhancements) && (
        <>
          <h1>{lang.productEnhancements}</h1>
          <DataMap
            data={contract.contractEnhancements}
            render={({ data: contractEnhancement }) => (
              <div key={contractEnhancement?.productEnhancementId}>
                <h2>{contractEnhancement?.productEnhancement?.name || lang.unknown}: </h2>
                <div>
                  {lang.price}:{' '}
                  <b>
                    <FormatValue value={`${(contractEnhancement.priceCAD || 0).toFixed(2)}$`} />
                  </b>
                </div>
                {!isDealerOrDistributor && contractEnhancement.originalPriceCAD !== contractEnhancement.priceCAD && (
                  <div>
                    {lang.basePrice}:{' '}
                    <b>
                      <FormatValue value={`${(contractEnhancement.originalPriceCAD || 0).toFixed(2)}$`} />
                    </b>
                  </div>
                )}
                {!isDealerOrDistributor && !!contractEnhancement.discountPriceCAD && (
                  <div>
                    {lang.adjustedPrice}:{' '}
                    <b>
                      <FormatValue value={`${(contractEnhancement.discountPriceCAD || 0).toFixed(2)}$`} />
                    </b>
                  </div>
                )}
                {!isDealer && !!contractEnhancement.distributorPriceCAD && (
                  <div>
                    {lang.distributorPrice}:{' '}
                    <b>
                      <FormatValue value={`${(contractEnhancement.distributorPriceCAD || 0).toFixed(2)}$`} />
                    </b>
                  </div>
                )}
                {!isDealerOrDistributor && !!values(contractEnhancement.pricingDistributions).length && (
                  <ObjectInfo
                    object={displayDistributions(contractEnhancement.pricingDistributions)}
                    objectName={lang.pricingDistributions}
                    disableTranslation
                  />
                )}
                {!isDealer && !!values(contractEnhancement.distributorDistributions).length && (
                  <ObjectInfo
                    object={displayDistributions(contractEnhancement.distributorDistributions)}
                    objectName={lang.distributorDistributions}
                    disableTranslation
                  />
                )}
              </div>
            )}
          />
          <hr />
          <div>
            {lang.total}:{' '}
            <b>
              <FormatValue value={`${sumBy(contract.contractEnhancements, (it) => it.priceCAD).toFixed(2)}$`} />
            </b>
          </div>
        </>
      )}

      <h1>{lang.other}</h1>

      <ObjectInfo
        object={pick(
          { ...contract, totalPrice: new Contract(contract).salePriceWithTaxCAD },
          'id',
          'legacyId',
          'appNumber',
          'effectiveDate',
          'paidOnLocalDate',
          'expiryDate',
          'createdAtLocal',
          'createdAtUtc',
          'modifiedAtUtc',
          'isPackage',
          'postSale',
          'totalPrice',
          'vertaforeStatus',
          'isContractTransferred'
        )}
        objectName={lang.contract}
      />
      {contract.statement && (
        <>
          <ObjectInfo
            object={pick(
              { ...contract.statement, statementNumber: `#${contract.statement.statementNumber}` },
              'id',
              'statementNumber'
            )}
            objectName={lang.statement}
            objectSubtitle={
              <Button
                onClick={() => {
                  generateStatementPdf(contract.statement?.id, contract.statement?.statementNumber);
                }}
                class={ThemeButtonCircle}
                media={<Icon class="fas-download" />}
                htmlElementProps={{
                  title: `${lang.download} ${lang.statement}#${contract.statement?.statementNumber}.pdf`,
                }}
              />
            }
          />
        </>
      )}
      {contract.contractConsent && (
        <ObjectInfo
          object={pick(
            {
              ...contract.contractConsent,
              status: isNull(contract.contractConsent.isAccepted)
                ? ContractConsentStatus.WAITING
                : contract.contractConsent.isAccepted
                  ? ContractConsentStatus.GIVEN
                  : ContractConsentStatus.REJECTED,
              dealerCostPrice: formatNumberWithSeparator(contract.contractConsent.dealerCost),
              term: `${contract.contractConsent.term} months`,
              totalRetailPrice: `${contract.contractConsent.salePriceCAD} + tax`,
            },
            'id',
            'type',
            'createdAtUtc',
            'modifiedAtUtc',
            'salePriceCAD',
            'dealerCostPrice',
            'term',
            'insurancePremiumCAD',
            'administrationFeeCAD',
            'limitOfLiability',
            'totalRetailPrice',
            'programName',
            'customerName',
            'customerEmail',
            'coverage',
            'dealership',
            'answeredAt',
            'isAccepted',
            'status'
          )}
          objectName={lang.consent}
        />
      )}
      <ObjectInfo
        object={pick(
          contract.client,
          'firstName',
          'lastName',
          'email',
          'phone',
          'streetAddress',
          'city',
          'provinceCode',
          'postalCode',
          'taxExempt',
          'vertaforeCustomerNumber',
          'vertaforeClientId'
        )}
        objectName={lang.client}
      />
      <ObjectInfo
        object={pick(
          {
            ...contract.vehicle,
            purchaseState:
              contract.vehicle?.purchaseState === PurchaseState.USED
                ? PurchaseState.PRE_OWNED
                : contract.vehicle?.purchaseState,
          },
          'vin',
          'year',
          'makeCode',
          'model',
          'bodyType',
          'engineType',
          'fuelType',
          'posOdometerReading',
          'stockNumber',
          'purchasePrice',
          'purchaseState',
          'purchaseType',
          'paymentCycle',
          'inServiceDate',
          'mfgWholeVehicleWarrantyMonths',
          'mfgWholeVehicleWarrantyKm',
          'mfgPowertrainWarrantyMonths',
          'mfgPowertrainWarrantyKm',
          'isNew',
        )}
        objectName={lang.vehicle}
      />
      {contract.vehicle?.lender && (
        <ObjectInfo
          object={pick(
            contract.vehicle?.lender,
            'name',
            'email',
            'phoneNumber',
            'streetAddress',
            'city',
            'provinceCode',
            'countryCode',
            'postalCode'
          )}
          objectName={lang.lender}
        />
      )}
      <ObjectInfo
        object={pick(
          {
            ...contract.dealer,
            primaryPhoneNumber: isValidPhoneNumber(contract.dealer.primaryPhoneNumber)
              ? contract.dealer.primaryPhoneNumber
              : contract.dealer.primaryPhoneNumber?.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3'),
          },
          'name',
          'primaryPhoneNumber',
          'primaryEmailAddress',
          'streetAddress',
          'city',
          'provinceCode',
          'countryCode',
          'postalCode'
        )}
        objectName={lang.dealer}
      />
    </>
  );
}
